import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { uploadImageFile } from 'api/imageAPI.js';
import { setSessionClient } from '../../features/session/sessionSlice';
import { createClient, updateClient, getClient, getTenantClient, getClientStats, getClients, getClientTenants, updateClientTenant, addClientTenant } from '../../api/clientAPI';
import { log, getStackTrace } from "@src/Log.init";

export const retrieveClient = createAsyncThunk(
  'client/retrieveClient',
  async (arg, { getState, dispatch }) => {
    const response = await getClient(getState().session.sessionToken, arg);
    await dispatch(setCurrentClient(response));
    return response;
  })

export const retrieveTenantClient = createAsyncThunk(
  'client/retrieveTenantClient',
  async (arg, { getState, dispatch }) => {
    const response = await getTenantClient(getState().session.sessionToken);
    await dispatch(setCurrentClient(response));
    return response;
  })
  
  export const retrieveClientStats = createAsyncThunk(
  'client/retrieveClientStats',
  async (arg, { getState, dispatch }) => {
    const response = await getClientStats(getState().session.sessionToken, arg);
    await dispatch(setClientStats(response));
    return response;
  })

  export const retrieveClientTenants = createAsyncThunk(
  'client/retrieveClientTenants',
  async (arg, { getState, dispatch }) => {
    const response = await getClientTenants(getState().session.sessionToken, arg);
    await dispatch(setClientTenants(response));
    return response;
  })

export const updateCurrentClient = createAsyncThunk(
  'client/updateCurrentClient',
  async (client, { getState, dispatch }) => {
    if (client.logo && typeof client.logo.name == 'string') // make sure this is file and not just the filename
      client.logo = await uploadImageFile(client._id, client.logo);
    const response = await updateClient(getState().session.sessionToken, client);
    await dispatch(resetCurrentClient(response));
    await dispatch(updateClientList(response));
    await dispatch(setSessionClient(response));
    return response;
  })

export const createNewClient = createAsyncThunk(
    'client/createNewClient',
    async (client, { getState, dispatch }) => {
        var logoFile = null;
        if (client.company.logo) {
          logoFile = client.company.logo; // remember the logo file
          client.company.logo = null;     // clear the logo for create
        }
        var response = await createClient(getState().session.sessionToken, client);
        if (logoFile != null) {  // upload the logo and update the client record with the logo file path
          response.logo = await uploadImageFile(response._id, logoFile);
          response = await updateClient(getState().session.sessionToken, response); // update to add the client logo
        }
        await dispatch(setCurrentClient(response));
        await dispatch(addClient(response));    // add the new client to the list of clients in state

    return response;
})
  
export const retrieveClients = createAsyncThunk(
'client/retrieveClients',
async (arg, { getState, dispatch }) => {
    const response = await getClients(getState().session.sessionToken);
    await dispatch(setClients(response));

    return response;
})

export const updateCurrentClientTenant = createAsyncThunk(
  'client/updateCurrentClientTenant',
  async (arg, { getState, dispatch }) => {
    const response = await updateClientTenant(getState().session.sessionToken, arg);
    await dispatch(updateClientTenantList(response));

    return response;
  })

export const addNewClientTenant = createAsyncThunk(
  'client/addNewClientTenant',
  async (arg, { getState, dispatch }) => {
    const response = await addClientTenant(getState().session.sessionToken, arg);
    await dispatch(addClientTenantToList(response));

    return response;
  })
  
export const clientSlice = createSlice({
  name: 'client',
  initialState: {
    tenantRetrievalStatus: null,
    currentClient: null,
    clients: [],
    clientTenants: [],
    clientStats: null,
    clientStatsRetrievalStatus: null
  },
  reducers: {
    setCurrentClient: (state, action) => {
        state.currentClient = action.payload;
        state.clientTenants = [];
    },
    resetCurrentClient: (state, action) => {
      state.currentClient = action.payload;
  },
    setClientStats: (state, action) => {
        state.clientStats = action.payload;
    },
    setClients: (state, action) => {
        state.clients = action.payload;
    },
    setClientTenants: (state, action) => {
        state.clientTenants = action.payload;
    },
    addClient: (state, action) => {
        state.clients.push(action.payload);
    },
    updateClientList: (state, action) => {
      var foundIndex = state.clients.findIndex(client => client._id === action.payload._id);
      state.clients[foundIndex] = action.payload;
    },
    updateClientTenantList: (state, action) => {
      var foundIndex = state.clientTenants.findIndex(tenant => tenant._id === action.payload._id);
      state.clientTenants[foundIndex] = action.payload;
    },
    addClientTenantToList: (state, action) => {
      state.clientTenants.push(action.payload);
    },
    setClientTenantStatus: (state, action) => {
      const clientTenant = state.clientTenants.find((ct) => ct.tenant_id === action.payload.tenant_id);
      if (clientTenant)
        clientTenant.status = action.payload.status;
    }
},
extraReducers: {
  [retrieveClient.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Retrieving client"});
  },
  [retrieveClient.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client retrieval successful"});
  },
  [retrieveClient.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client retrieval failed", meta_data: getStackTrace()});
  },
  [retrieveTenantClient.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Tenant: Retrieving client"});
  },
  [retrieveTenantClient.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Tenant client retrieval successful"});
  },
  [retrieveTenantClient.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Tenant client retrieval failed", meta_data: getStackTrace()});
  },
  [retrieveClientStats.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Retrieving client statistics"});
    state.clientStatsRetrievalStatus = "pending";
  },
  [retrieveClientStats.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client statistics retrieval successful"});
    state.clientStatsRetrievalStatus = "success";
  },
  [retrieveClientStats.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client statistics retrieval failed", meta_data: getStackTrace()});
    state.clientStatsRetrievalStatus = "failure";
  },
  [retrieveClients.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Retrieving clients"});
  },
  [retrieveClients.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Clients retrieval successful"});
  },
  [retrieveClients.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Clients retrieval failed", meta_data: getStackTrace()});
  },
  [retrieveClientTenants.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Client Tenant: Retrieving client tenants"});
    state.tenantRetrievalStatus = "pending";
  },
  [retrieveClientTenants.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client tenant retrieval successful"});
    state.tenantRetrievalStatus = "success";
  },
  [retrieveClientTenants.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client tenant retrieval failed", meta_data: getStackTrace()});
    state.tenantRetrievalStatus = "failure";
  },
  [createNewClient.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Creating client"});
  },
  [createNewClient.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client creation successful"});
  },
  [createNewClient.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client creation failed", meta_data: getStackTrace()});
  },
  [updateCurrentClient.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Updating current client"});
  },
  [updateCurrentClient.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client update successful"});
  },
  [updateCurrentClient.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client update failed", meta_data: getStackTrace()});
  },
  [updateCurrentClientTenant.pending]: (state, action) => {
    log.debug({message_type: "Client", message: "Updating current tenant"});
  },
  [updateCurrentClientTenant.fulfilled]: (state, action) => {
    log.info({message_type: "Client", message: "Client update tenant"});
  },
  [updateCurrentClientTenant.rejected]: (state, action) => {
    log.error({message_type: "SystemError", message: "Client update tenant", meta_data: getStackTrace()});
  },
},
});

export const { setCurrentClient,
               resetCurrentClient,
               setClients,
               setClientStats,
               setClientTenants,
               setClientTenantStatus,
               addClient,
               updateClientList,
               updateClientTenantList,
               addClientTenantToList
             } = clientSlice.actions;
         
export const selectCurrentClient = state => state.client.currentClient;
export const selectClients = state => state.client.clients;
export const selectClientTenants = state => state.client.clientTenants;
export const selectTenantRetrievalStatus = state => state.client.tenantRetrievalStatus;
export const selectClientStats = state => state.client.clientStats;
export const selectClientStatsRetrievalStatus = state => state.client.clientStatsRetrievalStatus;

export default clientSlice.reducer;
